import React, { useState, useEffect, ReactNode } from 'react';
import cn from 'classnames';
import usePrevious from '@hooks/usePrevious';
import ModalHeader from './SlideModalHeader';
import styles from './slide.module.scss';

const TRANSITION_DURATION_MS = 200; // see styles

type SlideModalProps = {
  children: ReactNode;
  open: boolean;
  handleClose: BaseAnyFunction;
  customOverlayClass?: string;
  showLogo?: boolean;
  closeStyles?: string;
  headerStyles?: string;
  contentStyles?: string;
  childrenStyles?: string;
  headerType?: string;
  customHeader?: React.ElementType;
  showCloseIcon?: boolean;
};

const SlideModal = ({
  children,
  customOverlayClass,
  showLogo,
  handleClose,
  closeStyles = '',
  headerStyles = '',
  contentStyles = '',
  childrenStyles = '',
  headerType,
  showCloseIcon = false,
  customHeader: CustomHeader,
  ...props
}: SlideModalProps) => {
  const [open, setOpen] = useState(false);
  const [appear, setAppear] = useState(false);

  const previousOpenProp = usePrevious<boolean>(props.open);

  useEffect(() => {
    const didOpenPropChange = Boolean(previousOpenProp) !== Boolean(props.open);
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    if (props.open) {
      timeoutId = setTimeout(() => {
        setAppear(true);
        setOpen(true);
      }, 0);
    } else if (!props.open && didOpenPropChange) {
      timeoutId = setTimeout(() => {
        setOpen(false);
      }, 0);
      setTimeout(() => {
        setAppear(false);
      }, TRANSITION_DURATION_MS);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [props.open, previousOpenProp]);

  const overlayClasses = cn({
    [styles.overlay]: true,
    [styles.slide]: open,
    [styles.appear]: appear,
    ...(customOverlayClass && { [customOverlayClass]: !!customOverlayClass }),
  });
  const contentClasses = cn({
    [styles.contentWrapper]: true,
    [contentStyles]: true,
  });

  const headerProps = {
    handleClose,
    buttonStyle: closeStyles,
    showLogo,
    headerStyles,
    headerType,
    showCloseIcon,
  };

  return (
    <div className={overlayClasses}>
      <div className={contentClasses}>
        {CustomHeader ? <CustomHeader {...headerProps} /> : <ModalHeader {...headerProps} />}
        <div className={childrenStyles}>{children}</div>
      </div>
    </div>
  );
};

export { HEADER_TYPES } from './constants';

export default SlideModal;
